/**
 * MediaPipe FaceMesh landmark index tables, and the EAR computation.
 *
 * Naming follows MediaPipe and ARKit: "right" is the *subject's* right eye,
 * which appears on the left of an un-mirrored image. The blendshape names
 * (`eyeBlinkRight`) use the same convention, so the two agree.
 */

import type { EyePair, Pose } from "./types.ts";

export interface Point2 {
  x: number;
  y: number;
}

/** Reads a landmark by its FaceMesh index, in pixel space. */
export type LandmarkReader = (index: number) => Point2;

/**
 * The six points of Soukupová & Čech (2016), in the order p1..p6 the formula
 * expects: p1 and p4 are the corners, p2/p3 upper lid, p6/p5 lower lid.
 */
export const EAR_INDICES: EyePair<readonly number[]> = {
  right: [33, 160, 158, 133, 153, 144],
  left: [362, 385, 387, 263, 373, 380],
};

/** Iris centres (present only when `refineLandmarks` is on). */
export const IRIS_CENTRE: EyePair<number> = { right: 468, left: 473 };

const RIGHT_EYE_RING = [
  33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246,
] as const;

const LEFT_EYE_RING = [
  362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398,
] as const;

const RIGHT_IRIS = [468, 469, 470, 471, 472] as const;
const LEFT_IRIS = [473, 474, 475, 476, 477] as const;

/**
 * The landmarks persisted into recordings, in a fixed order that must never be
 * reordered — fixtures on disk are indexed by position in this array.
 *
 * Storing the whole eye region rather than just the six EAR points is what lets
 * a future change to EAR_INDICES or to the formula itself be re-evaluated
 * against recordings captured today. 42 points ≈ 2.5 KB/s at 15fps.
 */
export const RECORDED_LANDMARKS: readonly number[] = [
  ...RIGHT_EYE_RING,
  ...LEFT_EYE_RING,
  ...RIGHT_IRIS,
  ...LEFT_IRIS,
];

const RECORDED_POSITION = new Map<number, number>(RECORDED_LANDMARKS.map((id, i) => [id, i]));

/** Builds a reader over the flat `eyeLandmarks` array carried by a SignalFrame. */
export function readerFromRecorded(flat: readonly number[]): LandmarkReader {
  return (index: number) => {
    const pos = RECORDED_POSITION.get(index);
    if (pos === undefined) {
      throw new Error(
        `Landmark ${index} is not in RECORDED_LANDMARKS, so it cannot be read back from a recording.`,
      );
    }
    return { x: flat[pos * 2] ?? Number.NaN, y: flat[pos * 2 + 1] ?? Number.NaN };
  };
}

/** Flattens the recorded subset into the array stored on a SignalFrame. */
export function packLandmarks(read: LandmarkReader): number[] {
  const out = new Array<number>(RECORDED_LANDMARKS.length * 2);
  for (let i = 0; i < RECORDED_LANDMARKS.length; i++) {
    const p = read(RECORDED_LANDMARKS[i] as number);
    out[i * 2] = p.x;
    out[i * 2 + 1] = p.y;
  }
  return out;
}

const dist = (a: Point2, b: Point2) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Eye Aspect Ratio.
 *
 * The reader MUST supply pixel-space coordinates. MediaPipe's normalised
 * landmarks scale x and y independently by image width and height, so computing
 * this on raw normalised values makes the result depend on the frame's aspect
 * ratio — the same eye then yields a different EAR on a 16:9 laptop and a
 * portrait phone, and every threshold derived from it silently stops meaning
 * the same thing.
 */
export function computeEAR(read: LandmarkReader, indices: readonly number[]): number {
  const [p1, p2, p3, p4, p5, p6] = indices.map((i) => read(i)) as [
    Point2,
    Point2,
    Point2,
    Point2,
    Point2,
    Point2,
  ];
  const horizontal = dist(p1, p4);
  if (!(horizontal > 0)) return Number.NaN;
  return (dist(p2, p6) + dist(p3, p5)) / (2 * horizontal);
}

export function computeEyeAspectRatios(read: LandmarkReader): EyePair<number> {
  return {
    left: computeEAR(read, EAR_INDICES.left),
    right: computeEAR(read, EAR_INDICES.right),
  };
}

/** Interpupillary distance in pixels — the crude screen-distance proxy (spec §2). */
export function computeIPD(read: LandmarkReader): number {
  return dist(read(IRIS_CENTRE.left), read(IRIS_CENTRE.right));
}

/**
 * A plausible-EAR guard. The spec insists the landmark indices be validated at
 * runtime rather than trusted from memory; a wrong table produces a number, not
 * an error, so without this check the whole pipeline fails silently.
 *
 * A human eye open at rest sits around 0.2–0.45. Anything far outside that on a
 * confidently-detected face means the index table is wrong.
 */
export function earLooksPlausible(ear: number): boolean {
  return Number.isFinite(ear) && ear > 0.02 && ear < 0.9;
}

/**
 * Euler angles from MediaPipe's 4x4 facial transformation matrix (column-major).
 *
 * ZYX intrinsic decomposition. The absolute convention matters less than being
 * consistent: these values are used for confidence gating by magnitude, not for
 * reconstructing orientation.
 */
export function poseFromMatrix(m: ArrayLike<number>): Pose {
  const r00 = m[0] as number;
  const r10 = m[1] as number;
  const r20 = m[2] as number;
  const r21 = m[6] as number;
  const r22 = m[10] as number;

  const sy = Math.hypot(r00, r10);
  if (sy > 1e-6) {
    return {
      pitch: Math.atan2(r21, r22),
      yaw: Math.atan2(-r20, sy),
      roll: Math.atan2(r10, r00),
    };
  }
  return {
    pitch: Math.atan2(-(m[9] as number), m[5] as number),
    yaw: Math.atan2(-r20, sy),
    roll: 0,
  };
}

export const ZERO_POSE: Pose = { pitch: 0, yaw: 0, roll: 0 };
